// orangeTheme.js
// Orange-themed design to match the BattleFace quote widget design
// Features warm orange/amber colors with professional typography

import { createTheme } from '@mui/material/styles';

const orangeTheme = createTheme({
  palette: {
    primary: {
      main: '#E67E22', // Orange color from the image
      light: '#F39C12',
      dark: '#D35400',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#F39C12', // Lighter orange
      light: '#F8C471',
      dark: '#E67E22',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#27AE60',
      light: '#58D68D',
      dark: '#229954',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#F39C12',
      light: '#F8C471',
      dark: '#E67E22',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#E74C3C',
      light: '#EC7063',
      dark: '#C0392B',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#3498DB',
      light: '#85C1E9',
      dark: '#2980B9',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FDFEFE',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2C3E50',
      secondary: '#5D6D7E',
    },
    grey: {
      50: '#FDFEFE',
      100: '#F8F9FA',
      200: '#E5E7E9',
      300: '#D5DBDB',
      400: '#A6ACAF',
      500: '#85929E',
      600: '#5D6D7E',
      700: '#515A5A',
      800: '#424949',
      900: '#2C3E50',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    h1: {
      fontWeight: 700,
      color: '#2C3E50',
    },
    h2: {
      fontWeight: 700,
      color: '#2C3E50',
    },
    h3: {
      fontWeight: 600,
      color: '#2C3E50',
    },
    h4: {
      fontWeight: 600,
      color: '#2C3E50',
    },
    h5: {
      fontWeight: 600,
      color: '#2C3E50',
    },
    h6: {
      fontWeight: 600,
      color: '#2C3E50',
    },
    subtitle1: {
      fontWeight: 500,
      color: '#2C3E50',
    },
    subtitle2: {
      fontWeight: 500,
      color: '#5D6D7E',
    },
    body1: {
      fontWeight: 400,
      lineHeight: 1.6,
      color: '#2C3E50',
    },
    body2: {
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#5D6D7E',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 8,
          padding: '12px 24px',
          fontSize: '16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 8px rgba(230, 126, 34, 0.3)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #E67E22 0%, #F39C12 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #D35400 0%, #E67E22 100%)',
          },
        },
        outlined: {
          borderColor: '#E67E22',
          color: '#E67E22',
          borderWidth: '2px',
          '&:hover': {
            borderColor: '#D35400',
            backgroundColor: 'rgba(230, 126, 34, 0.04)',
            borderWidth: '2px',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#E67E22',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#E67E22',
              borderWidth: 2,
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#E67E22',
          },
        },
      },
    },
    MuiBox: {
      styleOverrides: {
        root: {
          // Custom styling for orange-themed boxes
          '&.orange-header-box': {
            background: 'linear-gradient(135deg, #E67E22 0%, #F39C12 100%)',
            color: '#FFFFFF',
            padding: '24px',
            borderRadius: '12px 12px 0 0',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23FFFFFF" fill-opacity="0.1"%3E%3Ccircle cx="50" cy="50" r="4"/%3E%3Ccircle cx="10" cy="10" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              borderRadius: '12px 12px 0 0',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          backgroundColor: '#D5F4E6',
          color: '#27AE60',
          borderRadius: 8,
        },
        standardError: {
          backgroundColor: '#FADBD8',
          color: '#E74C3C',
          borderRadius: 8,
        },
        standardWarning: {
          backgroundColor: '#FCF3CF',
          color: '#F39C12',
          borderRadius: 8,
        },
        standardInfo: {
          backgroundColor: '#D6EAF8',
          color: '#3498DB',
          borderRadius: 8,
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          '& .MuiStepLabel-root .Mui-completed': {
            color: '#27AE60',
          },
          '& .MuiStepLabel-root .Mui-active': {
            color: '#E67E22',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#E5E7E9',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#E67E22',
          '&.Mui-checked': {
            color: '#E67E22',
          },
        },
      },
    },
  },
});

export default orangeTheme;
